"use client"

import * as React from "react"
import { useNavigate } from "react-router"
import { EmptyCell } from "@/components/data-views"
import { DataTable } from "@/components/data-table"
import type { ColumnDef } from "@/components/data-table/types"
import type { EvaluationWorkQueueRow } from "@/lib/mock/evaluation-work-queue"
import { useProduct } from "@/contexts/product-context"
import {
  WorkQueueResponseCell,
  WorkQueueRowActionsCell,
  WorkQueueStateCell,
  EVAL_TABLE_ACTION_COLUMN_SINGLE,
} from "../evaluation-term-table-cells"

type OperationsTableRow = EvaluationWorkQueueRow & Record<string, unknown>

function buildColumns(
  onNavigate: (path: string) => void,
  product: ReturnType<typeof useProduct>["product"],
): ColumnDef<OperationsTableRow>[] {
  return [
    {
      key: "subject",
      label: "Subject",
      width: 140,
      minWidth: 120,
      sortable: true,
      sortKey: "subject",
      cellKind: "text",
      cell: row => <span className="text-sm text-foreground">{row.subject}</span>,
    },
    {
      key: "state",
      label: "Stage",
      width: 168,
      minWidth: 140,
      sortable: true,
      sortKey: "stateLabel",
      cellKind: "status",
      cell: row => <WorkQueueStateCell row={row} />,
    },
    {
      key: "responseRate",
      label: "Response",
      width: 176,
      minWidth: 164,
      sortable: true,
      sortKey: "responseRate",
      cellKind: "progress",
      cell: row => <WorkQueueResponseCell row={row} />,
    },
    {
      key: "dueLabel",
      label: "Due",
      width: 160,
      minWidth: 120,
      sortable: true,
      sortKey: "dueLabel",
      cellKind: "text",
      cell: row =>
        row.dueLabel ? (
          <span className="text-sm text-muted-foreground">{row.dueLabel}</span>
        ) : (
          <EmptyCell />
        ),
    },
    {
      key: "action",
      label: "Action",
      ...EVAL_TABLE_ACTION_COLUMN_SINGLE,
      cell: row => (
        <WorkQueueRowActionsCell row={row} product={product} onNavigate={onNavigate} />
      ),
    },
  ]
}

interface OperationsTermTableProps {
  termId: string
  rows: EvaluationWorkQueueRow[]
}

export function OperationsTermTable({ termId, rows }: OperationsTermTableProps) {
  const { product } = useProduct()
  const navigate = useNavigate()
  const onNavigate = React.useCallback((path: string) => navigate(path), [navigate])
  const columns = React.useMemo(
    () => buildColumns(onNavigate, product),
    [onNavigate, product],
  )

  void termId

  return (
    <DataTable<OperationsTableRow>
      data={rows as OperationsTableRow[]}
      columns={columns}
      getRowId={row => row.id}
      selectable={false}
      searchable={false}
      showQueryControls={false}
      edgeInset={false}
      emptyState="No survey actions for this term."
    />
  )
}
